<template>
  <div
    v-show="isOpen"
    ref="dropdownBody"
    class="dropdown-container"
    :style="{
      left: dropdownButtonLeftValue ? `${leftAdjustment}px` : undefined,
      bottom: bottomAdjustment ? `${bottomAdjustment}px` : undefined
    }"
  >
    <div ref="headerContainer" class="header">
      <slot name="header"></slot>
      <div v-if="includeHeaderDivider && slots.header" class="dropdown-divider"></div>
    </div>
    <div class="dropdown-body">
      <slot name="body"></slot>
    </div>
    <div ref="footerContainer" class="footer">
      <div v-if="includeFooterDivider && slots.footer" class="dropdown-divider"></div>
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, toRefs, watch, onMounted, useSlots, onUnmounted } from 'vue';
  import { useDocumentProperties } from '@/composables/useDocumentProperties';
  import { useBoundingClientRect } from '@/composables/useBoundingClientRect';

  const props = withDefaults(defineProps<{
    isOpen: boolean;
    nodesExcludedFromClosingOnOutsideClick: Node[];
    dropdownButtonLeftValue?: number;
    dropdownMenuToggleButtonNode?: HTMLElement;
    includeHeaderDivider?: boolean;
    includeFooterDivider?: boolean;
  }>(), {
    includeHeaderDivider: true,
    includeFooterDivider: true
  });

  const slots = useSlots();
  const emit = defineEmits<{
    (e: 'update:maxBodyHeight', maxBodyHeight: number): void;
    (e: 'update:isOpen', isOpen: boolean): void;
  }>();

  const MIN_PIXELS_BETWEEN_MENU_AND_WINDOW_EDGE = 10;
  const {
    isOpen,
    nodesExcludedFromClosingOnOutsideClick,
    dropdownButtonLeftValue,
    dropdownMenuToggleButtonNode,
    includeFooterDivider,
    includeHeaderDivider
  } = toRefs(props);
  const { windowHeight, clientWidth } = useDocumentProperties();
  const { getBoundingClientRectPropertyRefs } = useBoundingClientRect();
  const dropdownBody = ref<HTMLDivElement>();
  const headerContainer = ref<HTMLDivElement>();
  const footerContainer = ref<HTMLDivElement>();
  const { height: headerHeight, update: updateHeaderContainerDimensionsAndLocation } = getBoundingClientRectPropertyRefs(headerContainer);
  const { height: footerHeight, update: updateFooterContainerDimensionsAndLocation } = getBoundingClientRectPropertyRefs(footerContainer);
  const { top: dropdownBodyTop, bottom: dropdownBodyBottom, width: dropdownBodyWidth, update: updateDropdownBodyDimensionsAndLocation } = getBoundingClientRectPropertyRefs(dropdownBody);
  const { top: toggleButtonTop, height: toggleButtonHeight, update: updateToggleButtonDimensionsAndLocation } = getBoundingClientRectPropertyRefs(dropdownMenuToggleButtonNode);

  const shouldOpenDropdownMenuUpwards = computed(() => {
    return (windowHeight.value / 2) < toggleButtonTop.value;
  });

  const leftAdjustment = computed(() => {
    if (!dropdownButtonLeftValue.value)
      return 0;

    return Math.min(0, clientWidth.value - dropdownButtonLeftValue.value - dropdownBodyWidth.value - MIN_PIXELS_BETWEEN_MENU_AND_WINDOW_EDGE);
  });

  const bottomAdjustment = computed(() => {
    if (!dropdownMenuToggleButtonNode.value || !shouldOpenDropdownMenuUpwards.value || !toggleButtonHeight.value)
      return null;

    return Math.max(0, toggleButtonHeight.value);
  });

  const dropdownHeight = computed(() => {
    if (dropdownMenuToggleButtonNode.value && shouldOpenDropdownMenuUpwards.value && toggleButtonHeight.value) {
      return windowHeight.value - headerHeight.value - footerHeight.value - (windowHeight.value - dropdownBodyBottom.value) - MIN_PIXELS_BETWEEN_MENU_AND_WINDOW_EDGE;
    }

    return windowHeight.value - headerHeight.value - footerHeight.value - dropdownBodyTop.value - MIN_PIXELS_BETWEEN_MENU_AND_WINDOW_EDGE;
  });

  function handleClickOutside(event: MouseEvent) {
    const targetElement = dropdownBody.value!;

    if (!(event.target instanceof Node)) {
      isOpen.value = false;
      return;
    }

    const target = event.target;
    const nodesExcludedFromClosingOnClick = [targetElement, ...nodesExcludedFromClosingOnOutsideClick.value];
    const hasClickedOutsideOfExcludedElements = nodesExcludedFromClosingOnClick.reduce((hasClickedOutside, node) => {
      return hasClickedOutside && !(node === target || node.contains(target));
    }, true);

    if (hasClickedOutsideOfExcludedElements)
      isOpen.value = false;
  }

  onMounted(() => {
    document.body.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.body.removeEventListener('click', handleClickOutside);
  });

  watch(dropdownHeight, () => {
    emit('update:maxBodyHeight', dropdownHeight.value);
  });

  watch(isOpen, () => {
    updateHeaderContainerDimensionsAndLocation();
    updateFooterContainerDimensionsAndLocation();
    updateDropdownBodyDimensionsAndLocation();
    updateToggleButtonDimensionsAndLocation();

    emit('update:isOpen', isOpen.value);
  });
</script>

<style>
  /*
    when/if we replace all instances of our old useDropdownMenu composable, we should keep the global css selectors in here for anything dropdown related, and remove the ones from App.vue (in my opinion).
    for now, all the selectors are preceded with div.dropdown-container to kind of scope these selectors
  */

  div.dropdown-container {
    background-color: #ffffff;
    z-index: 999;
    max-width: v-bind('`calc(95vw - ${2 * MIN_PIXELS_BETWEEN_MENU_AND_WINDOW_EDGE}px)`');
    font-size: 16px;
    position: absolute;
    padding: 3px 0px;
    border: 1px solid #c0ced8;
    opacity: 1.0;
    transition: opacity 0.3s, max-height 0.6s;
    background-clip: padding-box;
    border-radius: 4px;
    box-shadow: 0 6px 12px rgb(0 0 0 / 18%);
  }

  .header:not(:first-child) {
    margin-top: 6px;
  }

  div.dropdown-container .header,
  div.dropdown-container .footer {
    display: flex;
    flex-direction: column;
  }

  div.dropdown-container .header > *,
  div.dropdown-container .footer > * {
    width: 100%;
  }

  .dropdown-body {
    overflow-y: auto;
  }

  div.dropdown-container ul {
    list-style: none;
    padding: 0px 0px 0px 0px;
    margin: 0px;
  }

  div.dropdown-container ul li {
    display: flex;
    background-color: #ffffff;
    position: relative;
  }

  div.dropdown-container li .ellipsis-overflow {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  div.dropdown-container li button,
  div.dropdown-container li a {
    display: block;
    padding: 0px 10px;
    margin: 0px;
    font-size: 14px;
    font-weight: 700;
    color: #5a6672;
    background-color: transparent;
    border: none;
    border-radius: 0px;
    width: 100%;
    height: 28px;
    text-align: left;
    white-space: nowrap;
    text-decoration: none;
    transition: all 0.35s ease;
    line-height: inherit;
  }

  div.dropdown-container li a {
    display: flex;
    align-items: center;
  }

  div.dropdown-container li:hover {
    background-color: #dfe7ec;
  }

  div.dropdown-container li button:not(:disabled),
  div.dropdown-container li a:not(:disabled) {
    color: #2d3339;
  }

  div.dropdown-container li.selected-dropdown-option {
    background-color: #c0ced8;
  }

  div.dropdown-container li.selected-dropdown-option button:not(:disabled),
  div.dropdown-container li.selected-dropdown-option a:not(:disabled) {
    color: black;
  }

  div.dropdown-container li.selected-dropdown-option button:not(:disabled) i,
  div.dropdown-container li.selected-dropdown-option a:not(:disabled) i {
    opacity: 1;
  }

  div.dropdown-container .dropdown-divider {
    margin: 3px 0px;
    background-color: #c0ced8;
    height: 1px;
  }

  div.dropdown-container .dropdown-divider.dropdown-divider-thick {
    height: 3px;
    background-color: #434c56;
  }

  div.dropdown-container * {
    display: block;
  }

  div.dropdown-container b,
  div.dropdown-container i {
    display: inline;
  }

  div.dropdown-container i.custom-scc-icon {
    display: inline-flex;
  }

  div.dropdown-container button.icon-on-row-hover-only i,
  div.dropdown-container a.icon-on-row-hover-only i {
    opacity: 0 !important;
  }

  div.dropdown-container li:hover button.icon-on-row-hover-only i,
  div.dropdown-container li:hover a.icon-on-row-hover-only i {
    opacity: 1 !important;
  }

  div.dropdown-container li button.icon-button,
  div.dropdown-container li a.icon-button {
    display: block;
    padding: 0px 3px;
    width: fit-content;
  }

  div.dropdown-container li a.icon-button {
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  div.dropdown-container li button:not(.icon-button),
  div.dropdown-container li a:not(.icon-button) {
    flex: 1;
  }

  div.dropdown-container li button.icon-button:last-child,
  div.dropdown-container li a.icon-button:last-child {
    padding-right: 10px;
  }

  div.dropdown-container li button.icon-button i,
  div.dropdown-container li a.icon-button i {
    display: inline-block;
  }
</style>
